import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/modules/auth/hooks/useAuth'
import { RoutesApi, WaypointResponse, CreateWaypointDto, UpdateWaypointDto, GetWaypointsQueryDto } from '@/shared/api/routes'

export function useWaypoints() {
  const { getToken } = useAuth()
  const [waypoints, setWaypoints] = useState<WaypointResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const routesApi = useCallback(() => new RoutesApi(getToken), [getToken])

  const fetchWaypoints = useCallback(async (query: GetWaypointsQueryDto) => {
    try {
      setLoading(true)
      setError(null)
      const api = routesApi()
      const data = await api.getWaypoints(query)
      setWaypoints(data || [])
      return data || []
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch waypoints'
      setError(errorMessage)
      setWaypoints([]) // Set empty array on error
      throw err
    } finally {
      setLoading(false)
    }
  }, [routesApi])

  const createWaypoint = useCallback(async (waypointData: CreateWaypointDto) => {
    try {
      setLoading(true)
      setError(null)
      const api = routesApi()
      const newWaypoint = await api.createWaypoint(waypointData)
      setWaypoints(prev => [...prev, newWaypoint])
      return newWaypoint
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create waypoint')
      throw err
    } finally {
      setLoading(false)
    }
  }, [routesApi])

  const updateWaypoint = useCallback(async (waypointId: string, updates: UpdateWaypointDto, routeId: string) => {
    try {
      setLoading(true)
      setError(null)
      const api = routesApi()
      const updatedWaypoint = await api.updateWaypoint(waypointId, updates, routeId)
      setWaypoints(prev => prev.map(waypoint => waypoint.id === waypointId ? updatedWaypoint : waypoint))
      return updatedWaypoint
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update waypoint')
      throw err
    } finally {
      setLoading(false)
    }
  }, [routesApi])

  const deleteWaypoint = useCallback(async (waypointId: string, routeId?: string) => {
    try {
      setLoading(true)
      setError(null)
      const api = routesApi()
      await api.deleteWaypoint(waypointId, routeId)
      setWaypoints(prev => prev.filter(waypoint => waypoint.id !== waypointId))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete waypoint')
      throw err
    } finally {
      setLoading(false)
    }
  }, [routesApi])

  const getWaypointsByTripAndRoute = useCallback(async (tripId: string, routeId: string) => {
    // Skip if routeId is invalid
    if (!routeId || routeId === 'undefined' || routeId === 'null') {
      return []
    }

    try {
      setLoading(true)
      setError(null)
      const api = routesApi()
      const data = await api.getWaypointsByTripAndRoute(tripId, routeId)
      // NOTE: Do NOT update the main waypoints state here
      // This function is used to get trip+route-specific waypoints for display
      // The main waypoints state should always contain ALL waypoints for the map view
      // Only return the data without modifying state
      return data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch waypoints')
      throw err
    } finally {
      setLoading(false)
    }
  }, [routesApi])

  const getAggregateWaypointsForRoute = useCallback(async (routeId: string) => {
    // Skip if routeId is invalid
    if (!routeId || routeId === 'undefined' || routeId === 'null') {
      return []
    }

    try {
      setLoading(true)
      setError(null)
      const api = routesApi()
      const data = await api.getAggregateWaypointsForRoute(routeId)
      // NOTE: Do NOT update the main waypoints state here
      // This function is used to get route-specific waypoints for timeline/display
      // The main waypoints state should always contain ALL waypoints for the map view
      // Only return the data without modifying state
      return data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch aggregate waypoints')
      throw err
    } finally {
      setLoading(false)
    }
  }, [routesApi])

  return {
    waypoints,
    loading,
    error,
    fetchWaypoints,
    createWaypoint,
    updateWaypoint,
    deleteWaypoint,
    getWaypointsByTripAndRoute,
    getAggregateWaypointsForRoute
  }
}
